import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import ModalTemplate from "~/common/components/ModalPortal/ModalTemplate";

import Form, { Group } from "~/common/components/Form";
import Select, {optionsGenerator} from "~/common/components/Select";
import Input from "~/common/components/Input";
import Textarea from "~/common/components/Textarea";
import NumberInput from "~/common/components/NumberInput";
import DatePicker from "~/common/components/DatePicker";

import "~/assets/sass/pages/ir/basicInfo.scss";
import { PlusIcon } from "~/assets/svg/MathIcon";
import SignatureSelectModal from "~/common/components/SignatureSelectModal";
import { useGlobalStore } from "~/store";
import { dateFormatter } from "~/utils/formatter";
import { range } from "~/utils/array";
import DynamicFields from "./DynamicFields";
import SingleImageUploader from "~/common/components/ImageUploader/SingleImageUploader";
import useRequest from "~/common/hooks/useRequest";
import {BIZ_TYPE, COUNTRY_TYPE, LISTED_TYPE} from "~/common/constants/form";
import useRepository from "~/common/hooks/useRepository";
import { USER_INFO } from "~/store/user/repository";
import useAlert from "~/common/hooks/useAlert";
import axios from "axios";
import * as env from "~/common/constants/env";
import { optionGeneratorByObjectValues } from "~/common/components/CustomSelect";
import { useTranslation } from "react-i18next";
import SignatureUploadModal from "~/pages/mypage/admin/SignaturePage/SignatureUploadModal";
import { API_STATE } from "~/common/constants/state";
import {UPLOAD_TYPE} from "~/common/constants/upload";
import Radio from "~/common/components/Radio";
import Address from "~/common/components/Address";
import MultiCheckboxSelect from "~/common/components/MultiCheckboxSelect";
import {useNavigate, useParams} from "react-router";
import {certTypeSelect} from "~/common/constants/type";

// export const biznumQuery = (data) => {
// 	return axios.patch(`${env.EM_COMPANY_API_URL}/company/num`, data);
// };

const Edit = ({ showViewPage, Header, companyInfo }) => {
	const { t } = useTranslation();
	const [
		{
			ir: {
				selectedIdx: irIdx,
				basicInfo: {
					IRBasicInfoAttraction: Attraction,
					IRBasicInfoSubsidy: Subsidy,
					...BasicInfo
				},
			},
			vc: { fields },
		},
	] = useGlobalStore();

	const { idx } = useParams();

	const [getUserInfo] = useRepository(USER_INFO);
	const CountryType = COUNTRY_TYPE();
	const basicForm = useForm({ defaultValues: { irIdx: parseInt(idx), Attraction, Subsidy, BasicInfo } });
	const values = basicForm.watch();

	const irMutation = useRequest("post", "ir/basicInfo/edit");
	const userSignatureRequest = useRequest("get", "user/signature");

	const [signatureModal, setSignatureModal] = useState(false);


	useEffect(() => {
		userSignatureRequest.singleCall();
	}, []);

	const submit = async (data) => {
		const paramData = {
			...data,
			BasicInfo: {
				...data.BasicInfo,
				employeeNum: data?.BasicInfo?.employeeNum ? parseInt(data.BasicInfo.employeeNum): 0,
				fields: data.BasicInfo.CompanyField.map(item => item.field)
			},
			irIdx: parseInt(idx)
		}
		irMutation
			.asyncCall(paramData)
			.then(() => {
				getUserInfo().then(showViewPage);
			})
			.catch((msg) => console.log(msg));
	};

	const handleSetCompanyInfoData = () => {
		const info = companyInfo?.company;
		if(info) {
			basicForm.setValue("BasicInfo.regNum", info?.regNum);
			basicForm.setValue("BasicInfo.name", info?.name ?? "");
			basicForm.setValue("BasicInfo.ceoName", info?.ceoName ?? "");
			basicForm.setValue("BasicInfo.sales", info?.sales ?? "");
			basicForm.setValue("BasicInfo.salesYear", info?.salesYear ?? null);
			basicForm.setValue("BasicInfo.bizType", info?.bizType ?? null);
			basicForm.setValue("BasicInfo.bizStatus", info?.bizStatus ?? "");
			basicForm.setValue("BasicInfo.estDate", info.estDate ? dateFormatter(info.estDate, "-"): null);
			basicForm.setValue("BasicInfo.employeeNum", info?.employeeNum ?? "");
			basicForm.setValue("BasicInfo.type", info?.type ?? "");
			basicForm.setValue("BasicInfo.country", info?.country ?? null);
			basicForm.setValue("BasicInfo.listed", info?.listed ?? null);
			basicForm.setValue("BasicInfo.ceoEmail", info?.ceoEmail ?? "");
			basicForm.setValue("BasicInfo.homepage", info?.homepage ?? "");
			basicForm.setValue("BasicInfo.ceoContact", info?.ceoContact ?? "");
			basicForm.setValue("BasicInfo.zipCode", info?.zipCode ?? "");
			basicForm.setValue("BasicInfo.address", info?.address ?? "");
			basicForm.setValue("BasicInfo.addressDetail", info?.addressDetail ?? "");
			basicForm.setValue("BasicInfo.CompanyField", info?.CompanyField ?? []);
			basicForm.setValue("BasicInfo.introduction", info?.introduction ?? "");
			basicForm.setValue("BasicInfo.signImg", companyInfo?.signature?.img ?? "");
		}
	}

	useEffect(() => {
		if(values.BasicInfo.type === "preliminary start-up") {
			basicForm.setValue("BasicInfo.regNum", "0000000000");
		}
		if(values.BasicInfo.type === "individual business" || values.BasicInfo.type === "corporate business") {
			basicForm.setValue("BasicInfo.regNum", companyInfo?.company.regNum);
		}
	}, [values.BasicInfo.type])

	const [showCancelAlert, CancelAlert] = useAlert();

	const buttonList = [
		<button
			type="button"
			className="btn__solid"
			color="gray"
			onClick={() => {
				showCancelAlert().then(() => showViewPage());
			}}
		>
			{t("button.cancel")}
		</button>,
		<button
			type="button"
			className="btn__solid"
			color="primary"
			onClick={basicForm.handleSubmit(submit)}
		>
			{t("button.save")}
		</button>,
	];

	const signature = basicForm.watch("BasicInfo.signImg");
	const setSignature = (signature) => {
		basicForm.setValue("BasicInfo.signImg", signature);
	};

	const MonthOption = range(1, 12).map((i) => ({
		label: `${i}${t("contants.form.LICENSE_PERIOD.month")}`,
		value: `${i}${t("contants.form.LICENSE_PERIOD.month")}`,
	}));

	const recentYearsOption = () => {
		const TODAY = new Date();
		const THIS_YEAR = TODAY.getFullYear();
		return range(THIS_YEAR - 10, THIS_YEAR)
			.reverse()
			.map((i) => ({ label: `${i}`, value: `${i}` }));
	};

	const BizType = BIZ_TYPE();

	useEffect(() => {
		companyInfo && handleSetCompanyInfoData()
	}, [companyInfo])


	return (
		<>
			<Header buttonList={buttonList} />
			<div className="ir-view">
				<div className="c__card basicInfo-edit">
					<Form useForm={basicForm}>
						<Input
							name="BasicInfo.irTitle"
							className="ir-title"
							validation={{
								maxLength: {
									value: 15,
									message: t("IR.IrDetail.BasicInfo.Edit.Index.textLimit"),
								},
							}}
							placeholder={t("IR.IrDetail.BasicInfo.Edit.IR-Title")}
							required
						/>
					</Form>
					<form className="data-form">
						<section className="input-group-section">
							<h3 className="section-title">{t("Company.Info.Header.sub-title")}</h3>
							<Form useForm={basicForm}>
								<Group
									label={t("Company.Info.registration-num")}
									className={"registration-num"}
									required
								>
									<NumberInput
										placeholder="000-00-00000"
										name="BasicInfo.regNum"
										type="company"
										required
										readOnly={values.BasicInfo.type === "preliminary start-up"}
									/>
								</Group>
								<Input label={t("Company.Info.companyName")} name="BasicInfo.name" required />
								<Input label={t("Company.Info.ceo")} name="BasicInfo.ceoName" required />
								<Group
									className={"sales-input-group"}
									label={
										<>
											{t("Company.Info.sales")}
											<span>{t("Company.unit")}</span>
										</>
									}
								>
									<NumberInput
										placeholder={t("Company.Info.Edit.salesNum")}
										name="BasicInfo.sales"
										max={999999999}
										string
										required
									/>
									<Select
										placeholder={t("Company.Info.Edit.salesYear")}
										name="BasicInfo.salesYear"
										options={recentYearsOption()}
										required
									/>
								</Group>
								<Input label={t("Company.Info.bizType")} name="BasicInfo.bizType" />
								<Input label={t("Company.Info.bizStatus")} name="BasicInfo.bizStatus" />
								<DatePicker label={t("Company.Info.estDate")} name="BasicInfo.estDate" required />
								<NumberInput
									label={t("Company.Info.employeeNum")}
									name="BasicInfo.employeeNum"
									className="required"
									required
									string
								/>
								{/*기업구분*/}
								<Select
									label={t("Company.Info.type")}
									name="BasicInfo.type"
									options={certTypeSelect}
									required
								/>
								<Select
									label={t("Company.Info.Edit.country")}
									name="BasicInfo.country"
									options={optionGeneratorByObjectValues(CountryType)}
								/>
								<Radio
									label={t("Company.Info.listed")}
									className=""
									name="BasicInfo.listed"
									options={optionsGenerator(LISTED_TYPE)}
									boolean
								/>
								<Input
									label={t("Company.Info.homepage")}
									name="BasicInfo.homepage"
									required
								/>
								<Input
									label={t("Company.Info.ceoEmail")}
									type="email"
									name="BasicInfo.ceoEmail"
									placeholder="sample@domain.com"
									validation={{
										pattern: {
											value: /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*\.[a-zA-Z]{1,10}$/i,
											message: t("Company.Info.Edit.emailMessage"),
										},
									}}
								/>
								<NumberInput
									label={t("Company.Info.ceoContact")}
									name="BasicInfo.ceoContact"
									type="phone"
									placeholder="010-0000-0000"
								/>
								<Address
									label={t("Company.Info.address")}
									name={{
										postCode: "BasicInfo.zipCode",
										address1: "BasicInfo.address",
										address2: "BasicInfo.addressDetail",
									}}
									required
								/>
								<Group
									className={"keyword-wrap"}
									label={t("Company.Info.field")}
									required
								>
									<MultiCheckboxSelect
										valueKey={"field"}
										name="BasicInfo.CompanyField"
										options={fields}
										selectionLimit={5}
										placeholder={t("Company.Info.Edit.fieldPlaceholder")}
										required={true}
										selectedValues={values?.BasicInfo?.CompanyField ?? []}
									/>
									<p className="notice">{t("Company.Info.Edit.notice")}</p>
								</Group>
							</Form>
						</section>
						<section className="company-desc">
							<h3 className="section-title required">{t("Company.Info.introduction")}</h3>
							<Form useForm={basicForm}>
								<Textarea
									name="BasicInfo.introduction"
									placeholder={t("Company.Info.Edit.placeholder")}
									required
								/>
							</Form>
						</section>
						<section className="input-group-section">
							<h3 className="section-title">
								{t("IR.IrDetail.BasicInfo.Edit.Index.title")}
							</h3>
							<Form useForm={basicForm}>
								<NumberInput
									label={t("IR.IrDetail.BasicInfo.Edit.Index.label.capital")}
									name="BasicInfo.capital"
									string
								/>
								<NumberInput
									label={t("IR.IrDetail.BasicInfo.Edit.Index.label.faceValue")}
									name="BasicInfo.faceValue"
									string
								/>
								<NumberInput
									label={t("IR.IrDetail.BasicInfo.Edit.Index.label.totalStock")}
									name="BasicInfo.totalStock"
									string
								/>
								<Select
									label={t("IR.IrDetail.BasicInfo.Edit.Index.label.settlement")}
									name="BasicInfo.settlement"
									options={MonthOption}
								/>
								<NumberInput
									label={t("IR.IrDetail.BasicInfo.Edit.Index.label.comValue")}
									name="BasicInfo.comValue"
									string
								/>
							</Form>
						</section>
						<section id="investment">
							<Form useForm={basicForm} className="sub-section vertical">
								<Textarea
									label={t("IR.IrDetail.BasicInfo.Edit.Index.label.fundUse")}
									name="BasicInfo.fundUse"
								/>
								<Textarea
									label={t("IR.IrDetail.BasicInfo.Edit.Index.label.exitPlan")}
									name="BasicInfo.exitPlan"
								/>
							</Form>
							<div className="c__form vertical sub-section">
								<DynamicFields
									useForm={basicForm}
									name="Attraction"
									title={t(
										"IR.IrDetail.BasicInfo.Edit.Index.dynamicFields.title1"
									)}
								/>
								<DynamicFields
									useForm={basicForm}
									name="Subsidy"
									title={t(
										"IR.IrDetail.BasicInfo.Edit.Index.dynamicFields.title2"
									)}
								/>
							</div>
						</section>
						<section>
							<div className="seal-area">
								<span className="seal-label">Signature</span>
								<div className="seal-uploader">
									{/*서명 등록전*/}
									{signature ? (
										<SingleImageUploader
											img={signature}
											setImg={setSignature}
											width={236}
											height={122}
											deletable={false}
											editable={true}
											handleEdit={(e) => {
												e.preventDefault();
												setSignatureModal(true);
											}}
										/>
									) : (
										<button
											className="dashed-btn plus-btn"
											type="button"
											onClick={() => setSignatureModal(true)}
										>
											<PlusIcon />
											{t("Shareholder.ShareholderDetail.Edit.Index.button")}
										</button>
									)}
								</div>
							</div>
						</section>
					</form>
				</div>
			</div>

			{/* 법인인감 등록하기 모달 */}
			<ModalTemplate
				modalState={signatureModal}
				setModalToggle={setSignatureModal}
				title={t("MyPage.admin.Signature.index.modalTitle")}
				width={500}
				height={380}
				className={"modal-signatureUpload"}
				closeable={false}
			>
				<SignatureUploadModal {...{
					getSignatureList: () => {},
					uploaderConstant: UPLOAD_TYPE.COMPANY_LOGO,
					setSignImg: setSignature
				}} />
			</ModalTemplate>

			{/* 법인인감 Policy 모달 */}
			{/*<ModalTemplate*/}
			{/*	title={t("MyPage.admin.Signature.index.modalTitle")}*/}
			{/*	modalState={digitalDataModal}*/}
			{/*	setModalToggle={setDigitalDataModalModal}*/}
			{/*	width={500}*/}
			{/*	height={380}*/}
			{/*	className="modal-signatureUpload"*/}
			{/*>*/}
			{/*	<SignatureUploadModal {...{ getSignatureList: () => {}, signatureListRequest }} />*/}
			{/*</ModalTemplate>*/}

			<CancelAlert
				message={t("IR.IrDetail.BasicInfo.Edit.Index.cancelAlert")}
				confirm={t("button.yes")}
				reject={t("button.No")}
				type={"warning"}
			/>
		</>
	);
};

export default Edit;
